<template>
  <div class="exam-history">
    <!-- 页面头部 -->
    <div class="page-header">
      <h2>归档</h2>
      <div class="header-actions">
        <el-button type="primary" icon="el-icon-refresh" @click="loadHistoryExams">刷新列表</el-button>
      </div>
    </div>

    <!-- 归档考试列表 -->
    <div class="exam-list" v-loading="loading">
      <div v-if="exams.length === 0" class="empty-state">
        <i class="el-icon-document"></i>
        <p>没有考试记录</p>
      </div>

      <el-row :gutter="20" v-else>
        <el-col :xs="24" :sm="12" :md="8" :lg="6" v-for="exam in exams" :key="exam.id">
          <div class="exam-card" @click="openExamDetail(exam)">
            <div class="exam-header">
              <h3>{{ exam.examName }}</h3>
              <div class="exam-actions">
                <el-button type="text" icon="el-icon-view" @click.stop="viewExamDetail(exam)" title="查看详情"></el-button>
                <el-button type="text" icon="el-icon-folder-remove" @click.stop="unarchiveExam(exam)" title="取消归档" style="color: #67c23a;"></el-button>
              </div>
            </div>
            <div class="exam-content">
              <p class="exam-description">{{ exam.examDescription || '暂无描述' }}</p>
              <div class="exam-meta">
                <span class="meta-item">
                  <i class="el-icon-time"></i>
                  {{ exam.examDuration }} 分钟
                </span>
                <span class="meta-item">
                  <i class="el-icon-user"></i>
                  {{ exam.totalStudents }} 人参与
                </span>
                <span class="meta-item">
                  <i class="el-icon-check"></i>
                  {{ exam.gradedStudents }} 人已批阅
                </span>
              </div>
            </div>
            <div class="exam-footer">
              <div class="exam-stats">
                <div class="stat-item">
                  <span class="stat-label">平均分</span>
                  <span class="stat-value">{{ exam.averageScore || 0 }}</span>
                </div>
                <div class="stat-item">
                  <span class="stat-label">及格率</span>
                  <span class="stat-value">{{ exam.passRate || 0 }}%</span>
                </div>
              </div>
              <span class="exam-time">{{ formatTime(exam.endTime) }}</span>
            </div>
          </div>
        </el-col>
      </el-row>
    </div>

    <!-- 考试详情对话框 -->
    <el-dialog
      title="考试详情"
      :visible.sync="examDetailDialogVisible"
      width="90%"
      :before-close="closeExamDetail"
    >
      <div class="exam-detail">
        <!-- 考试基本信息 -->
        <div class="exam-info">
          <h3>{{ currentExam.examName }}</h3>
          <div class="info-grid">
            <div class="info-item">
              <label>考试时间：</label>
              <span>{{ formatTime(currentExam.startTime) }} - {{ formatTime(currentExam.endTime) }}</span>
            </div>
            <div class="info-item">
              <label>考试时长：</label>
              <span>{{ currentExam.examDuration }} 分钟</span>
            </div>
            <div class="info-item">
              <label>总分：</label>
              <span>{{ currentExam.totalScore }} 分</span>
            </div>
            <div class="info-item">
              <label>及格分：</label>
              <span>{{ currentExam.passScore }} 分</span>
            </div>
            <div class="info-item">
              <label>参与人数：</label>
              <span>{{ currentExam.totalStudents }} 人</span>
            </div>
            <div class="info-item">
              <label>批阅完成：</label>
              <span>{{ currentExam.gradedStudents }} 人</span>
            </div>
          </div>
        </div>

        <!-- 统计信息 -->
        <div class="exam-statistics">
          <h4>考试统计</h4>
          <el-row :gutter="20">
            <el-col :span="6">
              <div class="stat-card">
                <div class="stat-number">{{ currentExam.averageScore || 0 }}</div>
                <div class="stat-label">平均分</div>
              </div>
            </el-col>
            <el-col :span="6">
              <div class="stat-card">
                <div class="stat-number">{{ currentExam.highestScore || 0 }}</div>
                <div class="stat-label">最高分</div>
              </div>
            </el-col>
            <el-col :span="6">
              <div class="stat-card">
                <div class="stat-number">{{ currentExam.lowestScore || 0 }}</div>
                <div class="stat-label">最低分</div>
              </div>
            </el-col>
            <el-col :span="6">
              <div class="stat-card">
                <div class="stat-number">{{ currentExam.passRate || 0 }}%</div>
                <div class="stat-label">及格率</div>
              </div>
            </el-col>
          </el-row>
        </div>

        <!-- 学生成绩列表 -->
        <div class="student-scores">
          <h4>学生成绩</h4>
          <div class="score-filters">
            <el-form :inline="true">
              <el-form-item label="班级">
                <el-select v-model="scoreFilters.classId" placeholder="请选择班级" clearable @change="filterScores">
                  <el-option
                    v-for="classItem in classes"
                    :key="classItem.id"
                    :label="classItem.className"
                    :value="classItem.id"
                  ></el-option>
                </el-select>
              </el-form-item>
              <el-form-item label="试卷代码">
                <el-select v-model="scoreFilters.paperCode" placeholder="请选择试卷代码" clearable @change="filterScores">
                  <el-option label="A卷" value="A"></el-option>
                  <el-option label="B卷" value="B"></el-option>
                  <el-option label="C卷" value="C"></el-option>
                </el-select>
              </el-form-item>
              <el-form-item label="成绩范围">
                <el-input-number v-model="scoreFilters.minScore" placeholder="最低分" :min="0" :max="currentExam.totalScore" @change="filterScores"></el-input-number>
                <span style="margin: 0 10px;">-</span>
                <el-input-number v-model="scoreFilters.maxScore" placeholder="最高分" :min="0" :max="currentExam.totalScore" @change="filterScores"></el-input-number>
              </el-form-item>
              <el-form-item>
                <el-button type="primary" @click="exportScores">导出成绩</el-button>
              </el-form-item>
            </el-form>
          </div>

          <el-table :data="filteredStudentScores" border v-loading="scoresLoading">
            <el-table-column type="index" label="序号" width="60"></el-table-column>
            <el-table-column prop="studentNumber" label="学号" width="120"></el-table-column>
            <el-table-column prop="studentName" label="姓名" width="100"></el-table-column>
            <el-table-column prop="className" label="班级" width="120"></el-table-column>
            <el-table-column prop="paperCode" label="试卷代码" width="100"></el-table-column>
            <el-table-column prop="finalScore" label="最终得分" width="100">
              <template slot-scope="scope">
                <span :class="getScoreClass(scope.row.finalScore, currentExam.passScore)">
                  {{ scope.row.finalScore }}
                </span>
              </template>
            </el-table-column>
            <el-table-column prop="autoScore" label="自动得分" width="100"></el-table-column>
            <el-table-column prop="manualScore" label="人工得分" width="100"></el-table-column>
            <el-table-column label="得分率" width="100">
              <template slot-scope="scope">
                {{ getScoreRate(scope.row.finalScore, currentExam.totalScore) }}%
              </template>
            </el-table-column>
            <el-table-column label="状态" width="100">
              <template slot-scope="scope">
                <el-tag :type="getStatusType(scope.row.finalScore, currentExam.passScore)" size="mini">
                  {{ getStatusText(scope.row.finalScore, currentExam.passScore) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="submitTime" label="提交时间" width="160">
              <template slot-scope="scope">
                {{ formatTime(scope.row.submitTime) }}
              </template>
            </el-table-column>
            <el-table-column label="操作" width="120">
              <template slot-scope="scope">
                <el-button type="text" @click="viewStudentPaper(scope.row)" v-if="!disablePaperPreview">查看试卷</el-button>
              </template>
            </el-table-column>
          </el-table>

          <div class="pagination">
            <el-pagination
              @size-change="handleScoreSizeChange"
              @current-change="handleScoreCurrentChange"
              :current-page="scorePagination.currentPage"
              :page-sizes="[10, 20, 50, 100]"
              :page-size="scorePagination.pageSize"
              layout="total, sizes, prev, pager, next, jumper"
              :total="scorePagination.total"
            ></el-pagination>
          </div>
        </div>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button @click="examDetailDialogVisible = false">关闭</el-button>
        <el-button type="info" icon="el-icon-setting" @click="editArchiveSettings(currentExam)">设置权限</el-button>
        <el-button type="primary" @click="exportScores">导出成绩</el-button>
      </div>
    </el-dialog>

    <!-- 学生试卷查看对话框 -->
    <el-dialog
      title="学生试卷"
      :visible.sync="studentPaperDialogVisible"
      width="80%"
    >
      <div class="student-paper">
        <!-- 学生信息 -->
        <div class="student-info">
          <h4>{{ currentStudent.studentName }} ({{ currentStudent.studentNumber }})</h4>
          <p>{{ currentStudent.className }} - {{ currentStudent.paperCode }}卷</p>
          <p>最终得分：<span :class="getScoreClass(currentStudent.finalScore, currentExam.passScore)">{{ currentStudent.finalScore }}</span> / {{ currentExam.totalScore }}</p>
        </div>

        <!-- 试卷图片显示区域 -->
        <div class="paper-images" v-if="processedPaperImages.length > 0">
          <div class="image-gallery">
            <div
              v-for="(image, index) in processedPaperImages"
              :key="index"
              class="image-item"
              @click="previewImage(image, index)"
            >
              <img :src="image" :alt="`试卷图片 ${index + 1}`" />
              <div class="image-overlay">
                <i class="el-icon-zoom-in"></i>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="empty-images">
          <i class="el-icon-picture"></i>
          <p>暂无试卷图片</p>
        </div>

        <!-- 答题详情 -->
        <div class="answer-details">
          <h4>答题详情</h4>
          <el-table :data="answerDetails" border size="mini">
            <el-table-column prop="questionOrder" label="题号" width="60"></el-table-column>
            <el-table-column prop="questionType" label="题型" width="80">
              <template slot-scope="scope">
                {{ getQuestionTypeText(scope.row.questionType) }}
              </template>
            </el-table-column>
            <el-table-column prop="questionScore" label="分值" width="60"></el-table-column>
            <el-table-column prop="studentAnswer" label="学生答案" min-width="150">
              <template slot-scope="scope">
                <div class="answer-content">
                  {{ scope.row.studentAnswer }}
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="correctAnswer" label="正确答案" min-width="150">
              <template slot-scope="scope">
                <div class="answer-content">
                  {{ scope.row.correctAnswer }}
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="finalScore" label="得分" width="80">
              <template slot-scope="scope">
                <span :class="getScoreClass(scope.row.finalScore, scope.row.questionScore)">
                  {{ scope.row.finalScore }}
                </span>
              </template>
            </el-table-column>
            <el-table-column prop="teacherComment" label="教师评语" min-width="150">
              <template slot-scope="scope">
                <div class="comment-content">
                  {{ scope.row.teacherComment || '-' }}
                </div>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <!-- 批阅标注 -->
        <div class="grading-annotations" v-if="gradingAnnotations.length > 0">
          <h4>批阅标注</h4>
          <div class="annotations-list">
            <div
              v-for="annotation in gradingAnnotations"
              :key="annotation.id"
              class="annotation-item"
            >
              <div class="annotation-info">
                <span class="annotation-type">{{ getAnnotationTypeText(annotation.annotationType) }}</span>
                <span class="annotation-position">位置: ({{ annotation.positionX }}, {{ annotation.positionY }})</span>
                <span class="annotation-time">{{ formatTime(annotation.gradedTime) }}</span>
              </div>
              <div class="annotation-content" v-if="annotation.annotationContent">
                {{ annotation.annotationContent }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button @click="studentPaperDialogVisible = false">关闭</el-button>
      </div>
    </el-dialog>

    <!-- 图片预览对话框 -->
    <el-dialog
      title="试卷图片预览"
      :visible.sync="imagePreviewVisible"
      width="80%"
      :before-close="closeImagePreview"
    >
      <div class="image-preview-container">
        <img
          :src="currentPreviewImage"
          :alt="`试卷图片 ${currentImageIndex + 1}`"
          class="preview-image"
        />
      </div>
      <div class="preview-actions">
        <el-button @click="prevImage" :disabled="currentImageIndex === 0">上一张</el-button>
        <span class="image-counter">{{ currentImageIndex + 1 }} / {{ processedPaperImages.length }}</span>
        <el-button @click="nextImage" :disabled="currentImageIndex === processedPaperImages.length - 1">下一张</el-button>
      </div>
    </el-dialog>

    <!-- 归档设置对话框 -->
    <el-dialog
      title="归档设置"
      :visible.sync="archiveSettingsDialogVisible"
      width="500px"
      :before-close="closeArchiveSettingsDialog"
    >
      <div class="archive-settings">
        <p class="settings-tip">请设置归档后学生可以访问的内容：</p>
        <el-form ref="archiveSettingsForm" :model="archiveSettings" label-width="150px">
          <el-form-item label="发布学生成绩">
            <el-switch v-model="archiveSettings.allowViewScore"></el-switch>
            <span class="form-item-tip">学生可以查看自己的考试成绩</span>
          </el-form-item>
          <el-form-item label="允许学生查看试卷">
            <el-switch v-model="archiveSettings.allowViewPaper"></el-switch>
            <span class="form-item-tip">学生可以查看试卷和批注</span>
          </el-form-item>
          <el-form-item label="允许学生记录错题">
            <el-switch v-model="archiveSettings.allowRecordMistake"></el-switch>
            <span class="form-item-tip">学生可以将错题添加到错题本</span>
          </el-form-item>
          <el-form-item label="允许学生查看答题详情">
            <el-switch v-model="archiveSettings.allowViewAnswerDetail"></el-switch>
            <span class="form-item-tip">学生可以查看详细的答题情况</span>
          </el-form-item>
        </el-form>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="closeArchiveSettingsDialog">取消</el-button>
        <el-button type="primary" @click="confirmArchiveSettings">保存设置</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { examApi } from '@/api/exam'
import { classPaging } from '@/api/class'

export default {
  name: 'ExamHistory',
  props: {
    courseId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      loading: false,
      scoresLoading: false,
      exams: [],
      classes: [],
      currentExam: {},
      currentStudent: {},
      studentScores: [],
      filteredStudentScores: [],
      answerDetails: [],
      gradingAnnotations: [],
      examDetailDialogVisible: false,
      studentPaperDialogVisible: false,
      // 图片预览相关
      imagePreviewVisible: false,
      currentPreviewImage: '',
      currentImageIndex: 0,
      processedPaperImages: [],
      // 临时开关：禁用试卷图片预览，避免触发 /api/file/preview 请求
      disablePaperPreview: true,
      scoreFilters: {
        classId: null,
        paperCode: null,
        minScore: null,
        maxScore: null
      },
      scorePagination: {
        currentPage: 1,
        pageSize: 20,
        total: 0
      },
      // 归档设置相关
      archiveSettingsDialogVisible: false,
      archiveSettings: {
        allowViewScore: true,
        allowViewPaper: true,
        allowRecordMistake: true,
        allowViewAnswerDetail: true
      },
      currentArchiveExam: null
    }
  },
  mounted() {
    // 不在mounted时自动加载数据，等待父组件调用
    this.loadClasses() // 班级数据可以预加载，因为不涉及考试数据
  },
  methods: {
    // 加载历史考试列表
    async loadHistoryExams() {
      this.loading = true
      try {
        const response = await examApi.getHistoryExams(this.courseId)
        this.exams = response.data || []

        // 如果没有历史考试，不显示错误信息，空状态组件会显示"没有考试记录"
        if (!this.exams || this.exams.length === 0) {
          // 不显示任何消息，让空状态组件处理
        }
      } catch (error) {
        // 检查是否是404错误（没有历史考试）还是其他错误
        if (error.response && error.response.status === 404) {
          this.exams = []
          // 不显示错误信息，让空状态组件显示"没有考试记录"
        } else {
          this.$message.error('加载历史考试列表失败')
        }
      } finally {
        this.loading = false
      }
    },

    // 加载数据（供父组件调用）
    async loadData() {
      await this.loadHistoryExams()
    },

    // 加载班级列表
    async loadClasses() {
      console.log('=== loadClasses 开始 ===')
      console.log('courseId:', this.courseId)
      try {
        // 获取课程关联的班级列表
        if (this.courseId) {
          console.log('使用 getGradesByCourseId API 获取课程关联的班级')
          const { getGradesByCourseId } = await import('@/api/course')
          const res = await getGradesByCourseId(this.courseId)
          console.log('getGradesByCourseId 响应:', res)

          if (res && res.code === 1 && res.data && res.data.length > 0) {
            // 直接使用 CourseGradeVO 中的数据，包含 gradeId 和 gradeName
            this.classes = res.data.map(item => ({
              id: item.gradeId,
              gradeId: item.gradeId,
              grade_id: item.gradeId,
              grade_name: item.gradeName,
              gradeName: item.gradeName,
              name: item.gradeName,
              className: item.gradeName, // 用于显示
              code: item.gradeCode
            }))
            console.log('获取课程关联班级成功:', this.classes)
          } else {
            console.warn('该课程没有关联的班级或获取失败:', res)
            this.classes = []
            this.$message.warning('该课程暂未关联任何班级')
          }
        } else {
          console.log('没有课程ID，无法获取班级列表')
          this.classes = []
          this.$message.warning('无法获取课程信息')
        }

        console.log('=== loadClasses 完成 ===')
      } catch (error) {
        console.error('获取班级列表失败:', error)
        this.$message.error('获取班级列表失败')
        this.classes = []
      }
    },

    // 打开考试详情
    openExamDetail(exam) {
      this.currentExam = exam
      this.examDetailDialogVisible = true
      this.loadStudentScores()
    },

    // 查看考试详情
    viewExamDetail(exam) {
      this.openExamDetail(exam)
    },

    // 取消归档考试
    async unarchiveExam(exam) {
      try {
        await this.$confirm('确定要取消归档这个考试吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'info'
        })

        await examApi.unarchiveExam(exam.id)
        this.$message.success('取消归档成功')
        this.loadHistoryExams()
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('取消归档失败')
        }
      }
    },

    // 加载学生成绩
    async loadStudentScores() {
      this.scoresLoading = true
      try {
        const response = await examApi.getExamStudentScores(this.currentExam.id, this.scorePagination)
        this.studentScores = response.data.records
        this.scorePagination.total = response.data.total
        this.filterScores()
      } catch (error) {
        this.$message.error('加载学生成绩失败')
      } finally {
        this.scoresLoading = false
      }
    },

    // 筛选成绩
    filterScores() {
      let filtered = [...this.studentScores]

      if (this.scoreFilters.classId) {
        filtered = filtered.filter(score => score.classId === this.scoreFilters.classId)
      }

      if (this.scoreFilters.paperCode) {
        filtered = filtered.filter(score => score.paperCode === this.scoreFilters.paperCode)
      }

      if (this.scoreFilters.minScore !== null) {
        filtered = filtered.filter(score => score.finalScore >= this.scoreFilters.minScore)
      }

      if (this.scoreFilters.maxScore !== null) {
        filtered = filtered.filter(score => score.finalScore <= this.scoreFilters.maxScore)
      }

      this.filteredStudentScores = filtered
    },

    // 查看学生试卷
    async viewStudentPaper(student) {
      if (this.disablePaperPreview) {
        return
      }
      this.currentStudent = student
      this.processPaperImages(student.paperImageUrl)
      this.studentPaperDialogVisible = true

      try {
        // 加载答题详情
        const answerResponse = await examApi.getStudentAnswerDetails(this.currentExam.id, student.studentId)
        this.answerDetails = answerResponse.data

        // 加载批阅标注
        const annotationResponse = await examApi.getGradingAnnotations(this.currentExam.id, student.studentId)
        this.gradingAnnotations = annotationResponse.data
      } catch (error) {
        this.$message.error('加载学生试卷详情失败')
      }
    },

    // 导出成绩
    async exportScores() {
      try {
        const response = await examApi.exportExamScores(this.currentExam.id, this.scoreFilters)
        // 创建下载链接
        const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `${this.currentExam.examName}_成绩单.xlsx`
        link.click()
        window.URL.revokeObjectURL(url)
        this.$message.success('导出成功')
      } catch (error) {
        this.$message.error('导出失败')
      }
    },

    // 关闭考试详情
    closeExamDetail() {
      this.examDetailDialogVisible = false
      this.currentExam = {}
      this.studentScores = []
      this.filteredStudentScores = []
      this.scoreFilters = {
        classId: null,
        paperCode: null,
        minScore: null,
        maxScore: null
      }
    },

    // 获取成绩样式类
    getScoreClass(score, passScore) {
      if (score >= passScore) return 'pass-score'
      return 'fail-score'
    },

    // 获取状态类型
    getStatusType(score, passScore) {
      return score >= passScore ? 'success' : 'danger'
    },

    // 获取状态文本
    getStatusText(score, passScore) {
      return score >= passScore ? '及格' : '不及格'
    },

    // 获取得分率
    getScoreRate(score, totalScore) {
      return totalScore > 0 ? Math.round((score / totalScore) * 100) : 0
    },

    // 获取题目类型文本
    getQuestionTypeText(type) {
      const typeMap = {
        1: '选择题',
        2: '填空题',
        3: '判断题',
        4: '简答题'
      }
      return typeMap[type] || '未知'
    },

    // 获取标注类型文本
    getAnnotationTypeText(type) {
      const typeMap = {
        1: '对号',
        2: '半对',
        3: '叉号',
        4: '文字标注'
      }
      return typeMap[type] || '未知'
    },

    // 格式化时间
    formatTime(time) {
      if (!time) return ''
      return new Date(time).toLocaleString()
    },

    // 分页相关方法
    handleScoreSizeChange(val) {
      this.scorePagination.pageSize = val
      this.loadStudentScores()
    },

    handleScoreCurrentChange(val) {
      this.scorePagination.currentPage = val
      this.loadStudentScores()
    },

    // 处理试卷图片URL，支持多张图片
    processPaperImages(imageUrl) {
      this.processedPaperImages = []

      if (!imageUrl) {
        return
      }

      // 检查是否是JSON数组格式（多张图片）
      if (imageUrl.startsWith('[') && imageUrl.endsWith(']')) {
        try {
          const imageArray = JSON.parse(imageUrl)
          this.processedPaperImages = imageArray.map(url => this.getFullImageUrl(url))
        } catch (e) {
          console.error('解析图片URL数组失败:', e)
          this.processedPaperImages = [this.getFullImageUrl(imageUrl)]
        }
      } else if (imageUrl.includes(',')) {
        // 逗号分隔的多个URL
        const urls = imageUrl.split(',').map(url => url.trim())
        this.processedPaperImages = urls.map(url => this.getFullImageUrl(url))
      } else {
        // 单张图片
        this.processedPaperImages = [this.getFullImageUrl(imageUrl)]
      }
    },

    // 获取完整的图片URL
    getFullImageUrl(imageUrl) {
      if (!imageUrl) return ''

      // 如果已经是完整URL，直接返回
      if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
        return imageUrl
      }

      // 如果是API预览接口，使用前端API基础URL
      if (imageUrl.startsWith('/api/file/preview')) {
        // 移除开头的 /api，因为 VUE_APP_BASE_API 已经包含了 /api
        const pathWithoutApi = imageUrl.substring(4) // 移除 '/api'
        return process.env.VUE_APP_BASE_API + pathWithoutApi
      }

      // 如果是MinIO路径，使用MinIO端口
      if (imageUrl.startsWith('/exam-papers/')) {
        return `http://localhost:9000${imageUrl}`
      }

      // 其他情况使用API端口
      return `http://localhost:8080${imageUrl}`
    },

    // 预览图片
    previewImage(image, index) {
      this.currentPreviewImage = image
      this.currentImageIndex = index
      this.imagePreviewVisible = true
    },

    // 关闭图片预览
    closeImagePreview() {
      this.imagePreviewVisible = false
    },

    // 上一张图片
    prevImage() {
      if (this.currentImageIndex > 0) {
        this.currentImageIndex--
        this.currentPreviewImage = this.processedPaperImages[this.currentImageIndex]
      }
    },

    // 下一张图片
    nextImage() {
      if (this.currentImageIndex < this.processedPaperImages.length - 1) {
        this.currentImageIndex++
        this.currentPreviewImage = this.processedPaperImages[this.currentImageIndex]
      }
    },

    // 编辑归档设置
    async editArchiveSettings(exam) {
      try {
        this.currentArchiveExam = exam

        // 调试：打印考试对象的权限字段
        console.log('考试权限字段:', {
          allowViewScore: exam.allowViewScore,
          allowViewPaper: exam.allowViewPaper,
          allowRecordMistake: exam.allowRecordMistake,
          allowViewAnswerDetail: exam.allowViewAnswerDetail
        })

        // 如果有归档设置数据，使用现有数据；否则使用默认值
        if (exam.allowViewScore !== undefined && exam.allowViewPaper !== undefined && exam.allowRecordMistake !== undefined) {
          this.archiveSettings = {
            allowViewScore: exam.allowViewScore === 1,
            allowViewPaper: exam.allowViewPaper === 1,
            allowRecordMistake: exam.allowRecordMistake === 1,
            allowViewAnswerDetail: exam.allowViewAnswerDetail === 1
          }
          console.log('使用现有权限设置:', this.archiveSettings)
        } else {
          // 使用默认值
          this.archiveSettings = {
            allowViewScore: true,
            allowViewPaper: true,
            allowRecordMistake: true,
            allowViewAnswerDetail: true
          }
          console.log('使用默认权限设置:', this.archiveSettings)
        }

        // 显示归档设置对话框
        this.archiveSettingsDialogVisible = true
      } catch (error) {
        this.$message.error('打开归档设置失败')
      }
    },

    // 确认保存归档设置
    async confirmArchiveSettings() {
      try {
        if (!this.currentArchiveExam) {
          this.$message.error('无法获取考试信息')
          return
        }

        // 调用更新归档设置API
        await examApi.updateArchiveSettings(this.currentArchiveExam.id, {
          allowViewScore: this.archiveSettings.allowViewScore ? 1 : 0,
          allowViewPaper: this.archiveSettings.allowViewPaper ? 1 : 0,
          allowRecordMistake: this.archiveSettings.allowRecordMistake ? 1 : 0,
          allowViewAnswerDetail: this.archiveSettings.allowViewAnswerDetail ? 1 : 0
        })

        this.$message.success('归档设置更新成功')

        // 关闭对话框
        this.archiveSettingsDialogVisible = false
        this.currentArchiveExam = null

        // 重新加载考试列表
        await this.loadHistoryExams()
      } catch (error) {
        this.$message.error('更新设置失败')
      }
    },

    // 关闭归档设置对话框
    closeArchiveSettingsDialog() {
      this.archiveSettingsDialogVisible = false
      this.currentArchiveExam = null
    }
  }
}
</script>

<style scoped>
.exam-history {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  color: #303133;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #909399;
}

.empty-state i {
  font-size: 64px;
  margin-bottom: 20px;
  display: block;
}

.exam-list {
  min-height: 400px;
}

.exam-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  padding: 20px;
  margin-bottom: 20px;
  cursor: pointer;
  transition: all 0.3s;
}

.exam-card:hover {
  box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.exam-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.exam-header h3 {
  margin: 0;
  color: #303133;
  font-size: 16px;
}

.exam-actions {
  display: flex;
  gap: 5px;
}

.exam-content {
  margin-bottom: 15px;
}

.exam-description {
  color: #606266;
  font-size: 14px;
  margin-bottom: 15px;
  line-height: 1.5;
}

.exam-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
}

.meta-item {
  display: flex;
  align-items: center;
  color: #909399;
  font-size: 12px;
}

.meta-item i {
  margin-right: 5px;
}

.exam-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 15px;
  border-top: 1px solid #f0f0f0;
}

.exam-stats {
  display: flex;
  gap: 20px;
}

.stat-item {
  text-align: center;
}

.stat-label {
  display: block;
  font-size: 12px;
  color: #909399;
  margin-bottom: 5px;
}

.stat-value {
  display: block;
  font-size: 16px;
  font-weight: bold;
  color: #409eff;
}

.exam-time {
  color: #909399;
  font-size: 12px;
}

.exam-detail {
  padding: 20px 0;
}

.exam-info {
  margin-bottom: 30px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.exam-info h3 {
  margin: 0 0 20px 0;
  color: #303133;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 15px;
}

.info-item {
  display: flex;
  align-items: center;
}

.info-item label {
  font-weight: bold;
  color: #606266;
  margin-right: 10px;
  min-width: 80px;
}

.info-item span {
  color: #303133;
}

.exam-statistics {
  margin-bottom: 30px;
}

.exam-statistics h4 {
  margin: 0 0 20px 0;
  color: #303133;
}

.stat-card {
  text-align: center;
  padding: 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.stat-number {
  font-size: 24px;
  font-weight: bold;
  color: #409eff;
  margin-bottom: 8px;
}

.stat-label {
  font-size: 14px;
  color: #606266;
}

.student-scores {
  margin-bottom: 20px;
}

.student-scores h4 {
  margin: 0 0 20px 0;
  color: #303133;
}

.score-filters {
  margin-bottom: 20px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 4px;
}

.pagination {
  text-align: center;
  margin-top: 20px;
}

.pass-score {
  color: #67c23a;
  font-weight: bold;
}

.fail-score {
  color: #f56c6c;
  font-weight: bold;
}

.student-paper {
  padding: 20px 0;
}

.student-info {
  margin-bottom: 30px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 4px;
}

.student-info h4 {
  margin: 0 0 10px 0;
  color: #303133;
}

.student-info p {
  margin: 5px 0;
  color: #606266;
}

.paper-image {
  margin-bottom: 30px;
  text-align: center;
}

.answer-details {
  margin-bottom: 30px;
}

.answer-details h4 {
  margin: 0 0 15px 0;
  color: #303133;
}

.answer-content {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.comment-content {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.grading-annotations {
  margin-bottom: 20px;
}

.grading-annotations h4 {
  margin: 0 0 15px 0;
  color: #303133;
}

.annotations-list {
  max-height: 300px;
  overflow-y: auto;
}

.annotation-item {
  margin-bottom: 15px;
  padding: 15px;
  background: #fff;
  border-radius: 4px;
  border: 1px solid #e4e7ed;
}

.annotation-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-size: 12px;
  color: #606266;
}

.annotation-type {
  font-weight: bold;
  color: #409eff;
}

.annotation-content {
  color: #303133;
  font-size: 14px;
}

.dialog-footer {
  text-align: right;
}

/* 试卷图片画廊样式 */
.paper-images {
  margin-bottom: 20px;
}

.image-gallery {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 15px;
  margin-top: 15px;
}

.image-item {
  position: relative;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s;
}

.image-item:hover {
  border-color: #409eff;
  box-shadow: 0 2px 12px 0 rgba(64, 158, 255, 0.2);
}

.image-item img {
  width: 100%;
  height: 150px;
  object-fit: cover;
  display: block;
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s;
}

.image-item:hover .image-overlay {
  opacity: 1;
}

.image-overlay i {
  color: white;
  font-size: 24px;
}

.empty-images {
  text-align: center;
  padding: 40px 20px;
  color: #909399;
}

.empty-images i {
  font-size: 48px;
  margin-bottom: 15px;
  display: block;
}

/* 图片预览样式 */
.image-preview-container {
  text-align: center;
  margin-bottom: 20px;
}

.preview-image {
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
}

.preview-actions {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
}

.image-counter {
  font-size: 16px;
  color: #606266;
  font-weight: 500;
}

/* 归档设置对话框样式 */
.archive-settings {
  padding: 20px 0;
}

.settings-tip {
  margin-bottom: 20px;
  color: #606266;
  font-size: 14px;
}

.form-item-tip {
  margin-left: 10px;
  color: #909399;
  font-size: 12px;
}
</style>
